import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import * as ToastPrimitive from "@radix-ui/react-toast";

import { cx } from "@/lib/cx";
import { playSfx } from "@/platform/sfx";
import { Button } from "@/ui/Button";
import { AlertCircleIcon, CheckCircleIcon, CloseIcon } from "@/ui/icons";
import { LAYER } from "./surface";

/**
 * Transient feedback, and the app's undo.
 *
 * UI-REVIEW P10: grepping `toast|snackbar` across the app returned zero matches,
 * and nothing anywhere could be undone — while every mutation already runs
 * optimistically with cache rollback, which means undo costs one function call.
 * The safety was on the cheap action (a modal in front of deleting one task) and
 * entirely absent from the expensive one (dragging a card into Archived by
 * mistake).
 *
 * So the shape of this API is deliberate: `toast.undo("Task deleted", restore)`
 * has to be the shortest thing to write, shorter than reaching for a confirm
 * dialog. A confirm is now the exception, reserved for what genuinely cannot be
 * reversed.
 *
 * Built on `@radix-ui/react-toast`, which brings the parts that are easy to get
 * wrong: the region is announced to a screen reader without stealing focus, it
 * is reachable from the keyboard with a hotkey, hovering or focusing a toast
 * pauses its timer, and the timer is restarted when the window regains focus so
 * an 8-second undo window is not spent in another app.
 *
 * ## What changed in the iOS pass
 *
 * The card was a `bg-raised` rectangle with a 3px coloured left border — a web
 * snackbar. It is now the iOS presentation: a `material-thick` capsule at
 * `rounded-surface`, a **leading status glyph** carrying the tone, and the action
 * as a plain tinted button on the trailing edge. The left rail is gone; a rail
 * is the list-selection channel in this app (`rail-selected`) and reusing it for
 * severity was borrowing a signal that already means something else.
 *
 * It arrives on the bounce spring (`--busee-motion-bounce` +
 * `ease-spring-bounce`, in `overlay.css`) — the one place IOS-DESIGN §6 permits
 * a visible overshoot, because a toast that slides in flatly is missed.
 */

/** How long an undo stays on screen. The whole point is that it is generous. */
export const UNDO_WINDOW_MS = 8_000;
const DEFAULT_DURATION_MS = 5_000;
/** Long enough for the exit animation; the record is dropped afterwards. */
const EXIT_GRACE_MS = 320;

/**
 * `focus` is the legacy name for the accent tone and is kept because call sites
 * use it; it now renders in the accent family rather than the old sage.
 */
export type ToastTone = "neutral" | "danger" | "focus";

export type ToastAction = {
  label: string;
  onAction: () => void;
};

export type ToastOptions = {
  /** A second line. Keep it to the detail the title could not carry. */
  description?: ReactNode;
  tone?: ToastTone;
  /** Milliseconds on screen. `Infinity` pins it until dismissed. */
  duration?: number;
  action?: ToastAction;
};

export type ToastApi = {
  /** Neutral confirmation. Returns the id, for `dismiss`. */
  show: (message: ReactNode, options?: ToastOptions) => string;
  /**
   * The one that matters: an action that already happened, with a way back.
   *
   * ```ts
   * await deleteTask(task.id);          // optimistic, already gone from the list
   * toast.undo("Task deleted", () => restoreTask(task));
   * ```
   */
  undo: (
    message: ReactNode,
    onUndo: () => void,
    options?: Omit<ToastOptions, "action">,
  ) => string;
  /** A failure. Stays twice as long, because it usually needs reading. */
  error: (message: ReactNode, options?: ToastOptions) => string;
  dismiss: (id: string) => void;
};

type ToastRecord = {
  id: string;
  message: ReactNode;
  description?: ReactNode;
  tone: ToastTone;
  duration: number;
  action?: ToastAction;
  open: boolean;
};

const ToastContext = createContext<ToastApi | null>(null);

/**
 * Access the toast API. Throws when the provider is missing.
 *
 * Deliberately a throw rather than a no-op fallback: a silently swallowed
 * `toast.undo` means a destructive action fired with no way back and nothing on
 * screen to say so, which is exactly the bug this component exists to prevent.
 */
export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) {
    throw new Error("useToast() requires <OverlayProvider> (or <ToastProvider>) above it.");
  }
  return api;
}

export type ToastProviderProps = {
  children: ReactNode;
  /**
   * Key that focuses the toast region. F8 is Radix's default and is free in
   * every browser Kobo runs in; F6 is taken by browser pane-cycling.
   */
  hotkey?: string[];
};

export function ToastProvider({ children, hotkey = ["F8"] }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const counter = useRef(0);

  const close = useCallback((id: string) => {
    setToasts((current) =>
      current.map((toast) => (toast.id === id ? { ...toast, open: false } : toast)),
    );
    // Radix animates the exit off `data-state="closed"`, so the record has to
    // outlive the close by the length of that animation.
    window.setTimeout(() => {
      setToasts((current) => current.filter((toast) => toast.id !== id));
    }, EXIT_GRACE_MS);
  }, []);

  const push = useCallback((record: Omit<ToastRecord, "id" | "open">) => {
    counter.current += 1;
    const id = `toast-${counter.current}`;
    setToasts((current) => [...current, { ...record, id, open: true }]);
    return id;
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      show: (message, options = {}) =>
        push({
          message,
          description: options.description,
          tone: options.tone ?? "neutral",
          duration: options.duration ?? DEFAULT_DURATION_MS,
          action: options.action,
        }),

      undo: (message, onUndo, options = {}) =>
        push({
          message,
          description: options.description,
          tone: options.tone ?? "neutral",
          duration: options.duration ?? UNDO_WINDOW_MS,
          action: { label: "Undo", onAction: onUndo },
        }),

      error: (message, options = {}) => {
        playSfx("error");
        return push({
          message,
          description: options.description,
          tone: options.tone ?? "danger",
          duration: options.duration ?? DEFAULT_DURATION_MS * 2,
          action: options.action,
        });
      },

      dismiss: close,
    }),
    [close, push],
  );

  return (
    <ToastContext.Provider value={api}>
      <ToastPrimitive.Provider
        swipeDirection="right"
        swipeThreshold={48}
        duration={DEFAULT_DURATION_MS}
      >
        {children}

        {toasts.map((toast) => (
          <ToastCard key={toast.id} toast={toast} onClose={() => close(toast.id)} />
        ))}

        <ToastPrimitive.Viewport
          hotkey={hotkey}
          label={`Notifications (${hotkey.join("+")})`}
          // Read by the peek, which marks everything else inert while it is a
          // bottom sheet: an undo toast has to stay reachable from inside a peek.
          data-busee-toast-viewport=""
          className={cx(
            "fixed right-0 bottom-0 flex w-full max-w-full flex-col-reverse gap-2",
            "sm:max-w-[25rem]",
            LAYER.toast,
          )}
          // The one piece of the shell the viewport has to dodge, published as a
          // variable because a `position: fixed` element cannot see it.
          //
          // `--busee-tabbar-h` is the phone's bottom navigation, and it is a
          // *measured* height that already includes the home-indicator inset —
          // so the safe-area term is the fallback for when there is no tab bar,
          // not something to add on top of it. Adding both is how an undo toast
          // ended up floating ~34px above the bar on a notched iPhone.
          //
          // The peek used to be dodged here too, via `--busee-peek-width`: it
          // was a rail on the right edge and a toast landed exactly on its
          // Save/Cancel footer. The peek is centred now, its footer is nowhere
          // near this corner, and toasts sit a layer above it either way.
          style={{
            padding: "var(--spacing-pad)",
            paddingBottom:
              "calc(var(--spacing-pad) + var(--busee-tabbar-h, env(safe-area-inset-bottom, 0px)))",
          }}
        />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}

/**
 * The tone lives in one leading glyph, not in the card's fill or its border.
 *
 * `neutral` has no glyph at all — most toasts are "that happened", and a status
 * mark on every one of them trains the eye to ignore the mark on the one that
 * matters. `danger` and the accent tone each get a filled disc, which is the iOS
 * convention for a state (a stroked glyph would read as a button).
 *
 * `text-accent` is legal on the glyph and would not be legal on the label beside
 * it: this is a 20px non-text mark at ≥ 3.06:1 on every surface, and the small
 * text next to it is `text-ink`.
 */
const TONE_GLYPH: Record<ToastTone, ReactNode> = {
  neutral: null,
  danger: <AlertCircleIcon className="size-5 text-danger" />,
  focus: <CheckCircleIcon className="size-5 text-accent-ink" />,
};

function ToastCard({ toast, onClose }: { toast: ToastRecord; onClose: () => void }) {
  const glyph = TONE_GLYPH[toast.tone];

  return (
    <ToastPrimitive.Root
      data-busee-anim="toast"
      open={toast.open}
      duration={Number.isFinite(toast.duration) ? toast.duration : Number.MAX_SAFE_INTEGER}
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}
      className={cx(
        // `material-thick`: a toast may land over a dialog, a peek or a photo,
        // and its own two lines have to stay readable over all three. The
        // hairline is what keeps the capsule's edge visible when the material
        // and whatever is behind it happen to match.
        "busee-toast flex cursor-grab items-center gap-3 rounded-surface border-2 border-ps1-orange bg-overlay-panel active:cursor-grabbing",
        "p-3 pl-4 text-ink shadow-sheet",
      )}
    >
      {glyph ? <span className="flex shrink-0 items-center">{glyph}</span> : null}

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <ToastPrimitive.Title className="text-label text-ink">
          {toast.message}
        </ToastPrimitive.Title>
        {toast.description ? (
          <ToastPrimitive.Description className="truncate text-caption text-muted">
            {toast.description}
          </ToastPrimitive.Description>
        ) : null}
      </div>

      {toast.action ? (
        // `altText` is what a screen reader is told to do instead of clicking,
        // and Radix requires it. Making it the same words as the button keeps
        // the two from drifting.
        <ToastPrimitive.Action
          asChild
          altText={toast.action.label}
          onClick={() => {
            toast.action?.onAction();
            onClose();
          }}
        >
          <Button
            variant="ghost"
            size="sm"
            className="shrink-0 text-accent-ink"
          >
            {toast.action.label}
          </Button>
        </ToastPrimitive.Action>
      ) : null}

      {/* Always present, even next to Undo: swipe is not available to a keyboard
          and is undiscoverable on a trackpad. */}
      <ToastPrimitive.Close asChild aria-label="Dismiss">
        <Button variant="ghost" size="sm" iconOnly className="shrink-0 text-faint">
          <CloseIcon className="size-4" />
        </Button>
      </ToastPrimitive.Close>
    </ToastPrimitive.Root>
  );
}
